import { useEffect, useRef, useCallback } from 'react';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';

interface UsePlaybackParams {
  // Radar playback state
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  currentIndex: number;
  setCurrentIndex: (index: number | ((prev: number) => number)) => void;
  extendedTilesLength: number;
  tilesLength: number;

  // Tomorrow.io playback state
  isTioPlaying: boolean;
  setIsTioPlaying: (playing: boolean) => void;
  tioCurrentIndex: number;
  setTioCurrentIndex: (index: number | ((prev: number) => number)) => void;
  tioTimestampsLength: number;

  // Shared settings
  fps: number;
  autoLoop: boolean;
  isEventActive: boolean;
  mapTileProvider: MapTileProvider;
}

interface UsePlaybackReturn {
  // Handlers
  handlePlayPause: () => void;
  handleTioPlayPause: () => void;
  handleBackward: () => void;
  handleForward: () => void;

  // Refs exposed for external use (e.g., resetting on event change)
  hasManuallyPausedRef: React.RefObject<boolean>;
  hasManuallyPausedTioRef: React.RefObject<boolean>;
  currentIndexRef: React.RefObject<number>;
  tioCurrentIndexRef: React.RefObject<number>;
}

/**
 * Custom hook to manage playback/animation for radar tiles and Tomorrow.io timestamps.
 * Handles animation loops, play/pause, forward/backward controls, and live loop mode.
 */
export function usePlayback({
  isPlaying,
  setIsPlaying,
  currentIndex,
  setCurrentIndex,
  extendedTilesLength,
  tilesLength,
  isTioPlaying,
  setIsTioPlaying,
  tioCurrentIndex,
  setTioCurrentIndex,
  tioTimestampsLength,
  fps,
  autoLoop,
  isEventActive,
  mapTileProvider,
}: UsePlaybackParams): UsePlaybackReturn {
  // Animation refs
  const animationRef = useRef<number | null>(null);
  const tioAnimationRef = useRef<number | null>(null);

  // Manual pause tracking
  const hasManuallyPausedRef = useRef<boolean>(false);
  const hasManuallyPausedTioRef = useRef<boolean>(false);

  // Index refs for synchronous access in interval callbacks
  const currentIndexRef = useRef<number>(currentIndex);
  const tioCurrentIndexRef = useRef<number>(tioCurrentIndex);

  // Length refs for synchronous access in interval callbacks
  const extendedTilesLengthRef = useRef<number>(extendedTilesLength);
  const tioTimestampsLengthRef = useRef<number>(tioTimestampsLength);

  // Settings refs for synchronous access in interval callbacks
  const autoLoopRef = useRef<boolean>(autoLoop);
  const isEventActiveRef = useRef<boolean>(isEventActive);

  // Previous state refs for detecting changes
  const previousTilesLengthRef = useRef<number>(0);
  const previousExtendedTilesLengthRef = useRef<number>(0);
  const previousTioTimestampsLengthRef = useRef<number>(0);
  const previousFpsRef = useRef<number>(fps);
  const previousTioFpsRef = useRef<number>(fps);

  // Sync refs with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    extendedTilesLengthRef.current = extendedTilesLength;
  }, [extendedTilesLength]);

  useEffect(() => {
    autoLoopRef.current = autoLoop;
  }, [autoLoop]);

  useEffect(() => {
    isEventActiveRef.current = isEventActive;
  }, [isEventActive]);

  useEffect(() => {
    tioTimestampsLengthRef.current = tioTimestampsLength;
  }, [tioTimestampsLength]);

  useEffect(() => {
    tioCurrentIndexRef.current = tioCurrentIndex;
  }, [tioCurrentIndex]);

  // Track when tiles first load to reset manual pause flag
  useEffect(() => {
    // Reset manual pause flag when tiles first load (transition from 0 to > 0)
    if (previousTilesLengthRef.current === 0 && tilesLength > 0) {
      hasManuallyPausedRef.current = false;
    }
    previousTilesLengthRef.current = tilesLength;
  }, [tilesLength]);

  // Track when tomorrow.io timestamps first load to reset manual pause flag
  useEffect(() => {
    // Reset manual pause flag when timestamps first load (transition from 0 to > 0)
    if (previousTioTimestampsLengthRef.current === 0 && tioTimestampsLength > 0) {
      hasManuallyPausedTioRef.current = false;
    }
  }, [tioTimestampsLength]);

  // Clamp currentIndex when extended tiles shrink (e.g. nowcast toggle off)
  useEffect(() => {
    if (extendedTilesLength > 0 && currentIndex >= extendedTilesLength) {
      const clampedIndex = extendedTilesLength - 1;
      setCurrentIndex(clampedIndex);
      currentIndexRef.current = clampedIndex;
    }
  }, [extendedTilesLength, currentIndex, setCurrentIndex]);

  // Handle tile refresh in live loop mode -- only clamp if out of bounds.
  // The data-fetching layer already maps the old timestamp to the closest
  // tile in the new list via playbackDock, so we must NOT unconditionally
  // reset to 0 here (that would override the dock choice and cause a
  // visible jump to frame 0 on every live refresh).
  useEffect(() => {
    if (isPlaying && isEventActive && autoLoop && extendedTilesLength > 0) {
      if (currentIndex >= extendedTilesLength) {
        const clamped = extendedTilesLength - 1;
        setCurrentIndex(clamped);
        currentIndexRef.current = clamped;
      }
    }

    previousExtendedTilesLengthRef.current = extendedTilesLength;
  }, [extendedTilesLength, isPlaying, isEventActive, autoLoop, currentIndex, setCurrentIndex]);

  // Handle tomorrow.io timestamps refresh in live loop mode - reset to first timestamp and continue playback
  useEffect(() => {
    // When timestamps refresh in live loop mode (length changes), reset to start and keep playing
    const timestampsRefreshed = previousTioTimestampsLengthRef.current > 0 &&
      previousTioTimestampsLengthRef.current !== tioTimestampsLength;

    if (isTioPlaying && isEventActive && autoLoop && tioTimestampsLength > 0) {
      // If timestamps refreshed or current index is out of bounds, reset to start
      if (timestampsRefreshed || tioCurrentIndex >= tioTimestampsLength) {
        setTioCurrentIndex(0);
        tioCurrentIndexRef.current = 0; // Update ref immediately to prevent race condition
        // Don't stop playback - we want it to continue
      }
    }

    // Update previous length
    previousTioTimestampsLengthRef.current = tioTimestampsLength;
  }, [tioTimestampsLength, isTioPlaying, isEventActive, autoLoop, tioCurrentIndex, setTioCurrentIndex]);

  // Animation loop (plays through realtime + nowcast tiles)
  useEffect(() => {
    // Only create/clear interval based on isPlaying state
    if (!isPlaying) {
      // Clear interval if it exists
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    // Don't recreate interval if it already exists (tiles refreshed but we want to keep playing)
    // The guard prevents recreation on tile refresh - interval continues with updated refs
    // However, if FPS changed, we need to restart the interval with the new frame rate
    if (animationRef.current !== null) {
      // If FPS changed, clear the interval to restart with new frame rate
      if (previousFpsRef.current !== fps) {
        clearInterval(animationRef.current);
        animationRef.current = null;
        previousFpsRef.current = fps;
      } else {
        // FPS hasn't changed, don't recreate (tile refresh case)
        return;
      }
    } else {
      previousFpsRef.current = fps;
    }

    // Only create interval if tiles are available (check ref, not state to avoid re-running on refresh)
    if (extendedTilesLengthRef.current === 0) {
      return;
    }

    // Cap radar playback at 5 fps to prevent shaking/flickering effects
    // Higher fps causes visual instability due to rapid layer switching in double-buffering
    const radarFps = Math.min(fps, 5);
    const frameDelay = 1000 / radarFps;

    // Create interval - uses refs so it always has the latest values
    animationRef.current = window.setInterval(() => {
      const currentIdx = currentIndexRef.current;
      const tilesLen = extendedTilesLengthRef.current;
      const shouldLoop = autoLoopRef.current && isEventActiveRef.current;

      if (tilesLen === 0) {
        // Only stop if we're not in live loop mode
        if (!shouldLoop) {
          if (animationRef.current) {
            clearInterval(animationRef.current);
            animationRef.current = null;
          }
          hasManuallyPausedRef.current = true;
          setIsPlaying(false);
        }
        return;
      }

      // If index is out of bounds (tiles may have refreshed)
      if (currentIdx >= tilesLen) {
        // In live loop mode, always reset to 0, never stop
        if (shouldLoop) {
          setCurrentIndex(0);
          currentIndexRef.current = 0; // Update ref immediately
          return;
        }
        // Otherwise, stop playback
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        hasManuallyPausedRef.current = true;
        setIsPlaying(false);
        return;
      }

      // If we've reached the end
      if (currentIdx >= tilesLen - 1) {
        // In live loop mode, restart from beginning
        if (shouldLoop) {
          setCurrentIndex(0);
          currentIndexRef.current = 0; // Update ref immediately
          return;
        }
        // Otherwise, stop playback
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
        hasManuallyPausedRef.current = true;
        setIsPlaying(false);
        return;
      }

      // Increment to next tile -- update ref immediately so the next tick
      // (and any concurrent render) sees the new value without waiting for
      // React's async state flush.
      const nextIdx = currentIdx + 1;
      currentIndexRef.current = nextIdx;
      setCurrentIndex(nextIdx);
    }, frameDelay);

    return () => {
      // Only clear interval when isPlaying becomes false or fps changes
      // Don't clear on tile refresh in live loop mode - check refs to determine if we should keep it running
      const shouldKeepRunning = isPlaying && autoLoopRef.current && isEventActiveRef.current && extendedTilesLengthRef.current > 0;
      if (!shouldKeepRunning && animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, fps, extendedTilesLength, setIsPlaying, setCurrentIndex]); // extendedTilesLength needed to create interval when tiles become available

  // Tomorrow.io animation loop
  // Use slower playback for Tomorrow.io tiles to allow time for tiles to load
  useEffect(() => {
    // Only create/clear interval based on isTioPlaying state
    if (!isTioPlaying || !isTioProvider(mapTileProvider)) {
      // Clear interval if it exists
      if (tioAnimationRef.current) {
        clearInterval(tioAnimationRef.current);
        tioAnimationRef.current = null;
      }
      return;
    }

    // Don't recreate interval if it already exists (timestamps refreshed but we want to keep playing)
    // The guard prevents recreation on timestamp refresh - interval continues with updated refs
    // However, if FPS changed, we need to restart the interval with the new frame rate
    if (tioAnimationRef.current !== null) {
      // If FPS changed, clear the interval to restart with new frame rate
      if (previousTioFpsRef.current !== fps) {
        clearInterval(tioAnimationRef.current);
        tioAnimationRef.current = null;
        previousTioFpsRef.current = fps;
      } else {
        // FPS hasn't changed, don't recreate (timestamp refresh case)
        return;
      }
    } else {
      previousTioFpsRef.current = fps;
    }

    // Only create interval if timestamps are available (check ref, not state to avoid re-running on refresh)
    if (tioTimestampsLengthRef.current === 0) {
      return;
    }

    // Use a slower frame rate for Tomorrow.io tiles to allow tiles to load
    // Tiles are fetched from an external API and need more time than pre-loaded radar tiles
    // Cap at 1 fps (1 second per frame) to give tiles time to load and render smoothly
    const tioFps = Math.min(fps, 1);
    const frameDelay = 1000 / tioFps;

    // Create interval - uses refs so it always has the latest values
    tioAnimationRef.current = window.setInterval(() => {
      const currentIdx = tioCurrentIndexRef.current;
      const timestampsLen = tioTimestampsLengthRef.current;
      const shouldLoop = autoLoopRef.current && isEventActiveRef.current;

      if (timestampsLen === 0) {
        // Only stop if we're not in live loop mode
        if (!shouldLoop) {
          if (tioAnimationRef.current) {
            clearInterval(tioAnimationRef.current);
            tioAnimationRef.current = null;
          }
          setIsTioPlaying(false);
          hasManuallyPausedTioRef.current = true; // Prevent auto-play from restarting
        }
        return;
      }

      // If index is out of bounds (timestamps may have refreshed)
      if (currentIdx >= timestampsLen) {
        // In live loop mode, always reset to 0, never stop
        if (shouldLoop) {
          setTioCurrentIndex(0);
          tioCurrentIndexRef.current = 0; // Update ref immediately
          return;
        }
        // Otherwise, stop playback
        if (tioAnimationRef.current) {
          clearInterval(tioAnimationRef.current);
          tioAnimationRef.current = null;
        }
        setIsTioPlaying(false);
        hasManuallyPausedTioRef.current = true; // Prevent auto-play from restarting
        return;
      }

      // If we've reached the end
      if (currentIdx >= timestampsLen - 1) {
        // In live loop mode, restart from beginning
        if (shouldLoop) {
          setTioCurrentIndex(0);
          tioCurrentIndexRef.current = 0; // Update ref immediately
          return;
        }
        // Otherwise, stop playback
        if (tioAnimationRef.current) {
          clearInterval(tioAnimationRef.current);
          tioAnimationRef.current = null;
        }
        setIsTioPlaying(false);
        hasManuallyPausedTioRef.current = true; // Prevent auto-play from restarting
        return;
      }

      // Increment to next timestamp
      setTioCurrentIndex(currentIdx + 1);
    }, frameDelay);

    return () => {
      // Only clear interval when isTioPlaying becomes false or fps changes
      // Don't clear on timestamp refresh in live loop mode - check refs to determine if we should keep it running
      const shouldKeepRunning = isTioPlaying && autoLoopRef.current && isEventActiveRef.current && tioTimestampsLengthRef.current > 0;
      if (!shouldKeepRunning && tioAnimationRef.current) {
        clearInterval(tioAnimationRef.current);
        tioAnimationRef.current = null;
      }
    };
  }, [isTioPlaying, fps, tioTimestampsLength, mapTileProvider, setIsTioPlaying, setTioCurrentIndex]); // tioTimestampsLength needed to create interval when timestamps become available

  // Handlers
  const handlePlayPause = useCallback(() => {
    const willBePlaying = !isPlaying;

    // If at the end and user wants to play, restart from the beginning
    if (willBePlaying && currentIndex >= extendedTilesLength - 1) {
      setCurrentIndex(0);
    }

    setIsPlaying(willBePlaying);
    // Track if user manually paused (not playing after click)
    if (!willBePlaying) {
      hasManuallyPausedRef.current = true;
    } else {
      // Reset when user manually plays (allows auto-play to work again)
      hasManuallyPausedRef.current = false;
    }
  }, [isPlaying, currentIndex, extendedTilesLength, setCurrentIndex, setIsPlaying]);

  const handleTioPlayPause = useCallback(() => {
    const willBePlaying = !isTioPlaying;

    // If at the end and user wants to play, restart from the beginning
    if (willBePlaying && tioCurrentIndex >= tioTimestampsLength - 1) {
      setTioCurrentIndex(0);
    }

    setIsTioPlaying(willBePlaying);
    // Track if user manually paused (not playing after click)
    if (!willBePlaying) {
      hasManuallyPausedTioRef.current = true;
    } else {
      // Reset when user manually plays (allows auto-play to work again)
      hasManuallyPausedTioRef.current = false;
    }
  }, [isTioPlaying, tioCurrentIndex, tioTimestampsLength, setTioCurrentIndex, setIsTioPlaying]);

  const handleBackward = useCallback(() => {
    setCurrentIndex((prev: number) => Math.max(0, prev - 1));
  }, [setCurrentIndex]);

  const handleForward = useCallback(() => {
    setCurrentIndex((prev: number) => Math.min(extendedTilesLength - 1, prev + 1));
  }, [extendedTilesLength, setCurrentIndex]);

  return {
    handlePlayPause,
    handleTioPlayPause,
    handleBackward,
    handleForward,
    hasManuallyPausedRef,
    hasManuallyPausedTioRef,
    currentIndexRef,
    tioCurrentIndexRef,
  };
}
